/*
 * Copyright (c) Kalaora 2022.
 * Author: fharold
 *
 * hilo-oauth-manager
 * HTTPService.ts
 */

import * as Axios from "axios";
import {Service} from "./Service";
import ApiHelper from "../ApiHelper";
import APIError from "../model/exception/APIError";
import AuthenticationService, {AuthenticationWatcher} from "./AuthenticationService";
import {AccessToken} from "../model/auth/AccessToken";
import Config from "../../config";

export interface APIdata {
    content: any
    resources: any
    page?: number;
    limit?: number;
    count?: number;
    total?: number;
}

export interface ProgressWatcher {
    onProgress: (progressEvent: any) => void;
}

export default class KareviHTTPService extends Service<any, any> implements AuthenticationWatcher {
    private readonly axios: Axios.AxiosInstance;
    private readonly _authenticationService: AuthenticationService;
    public static id = "KareviHTTPService"

    constructor(authenticationService: AuthenticationService) {
        super();

        this._authenticationService = authenticationService;

        this.axios = Axios.default.create({
            baseURL: Config.KAREVI_API_URL,
            timeout: 30000,
            maxContentLength: 1024 * 1024 * 50
        });
    }

    /**
     * authenticationRefreshed
     * @param token
     */
    public authenticationRefreshed(token?: AccessToken) {
        if (token) {
            this.axios.defaults.headers.common['Authorization'] = `Bearer ${token.access_token}`;
        }
    }

    /**
     * Axios default
     * @description Returns the default API HTTPService instance
     */
    public get default() {
        return this.axios;
    }

    public start() {
        super.start();

        this._authenticationService.addListener(this);
    }

    public stop() {
        if (!this._authenticationService) {
            return;
        }

        this._authenticationService.removeListener(this);
    }

    /**
     * generateURL
     * @param endpoint Endpoint route to generate url with
     * @returns string of the generated URL
     */
    public generateURL(endpoint: string): string {
        if (!endpoint || !this._authenticationService?.token) {
            throw new Error('Unable to generate endpoint URL');
        }

        let safeEndpoint = endpoint;
        if (safeEndpoint[0] !== '/') {
            safeEndpoint = `/${safeEndpoint}`
        }

        return `${Config.KAREVI_API_URL}${safeEndpoint}`;
    }

    /**
     * buildRoute
     * @param route string defining the template route to build (e.g. /user/{userId})
     * @param urlParams object defining values that must be placed directly in route (e.g. { userId: 1 })
     *
     * @description Replaces template parameters in *route* matching them with values contained in *urlParams*
     */
    public buildRoute(route: string, urlParams: any, withToken: boolean = false) {
        for (const key of Object.keys(urlParams)) {
            route = route.replace(`{${key}}`, urlParams[key] as string);
        }

        if (withToken === true) {
            const token = this.default.defaults.headers.common['Authorization'].replace('Bearer ', '');
            route = `${route}?access_token=${token}`;
        }

        return route;
    }

    /**
     * Internal route performing HTTP simple get
     * @param endpoint route endpoint (e.g. '/user/1')
     */
    public async get(endpoint: string): Promise<APIdata> {
        let url = this.generateURL(endpoint);
        try {


            let response = await this.default.get(url, {
                headers: {
                    "Authorization": `Bearer ${this._authenticationService.token?.access_token || ""}`
                }
            });
            ApiHelper.handleAPIErrorIfNecessary(response);
            return response.data as APIdata;
        } catch (e) {
            ApiHelper.handleAPIErrorIfNecessary(e?.response || "response not defined");
            throw new APIError("Unknown error", 0, e.toString());
        }
    }

    /**
     * getRaw
     * @description Returns raw results from API
     * @param endpoint string
     */
    public async getRaw(endpoint: string): Promise<any> {
        let url = this.generateURL(endpoint);
        try {
            let response = await this.default.get(url);

            ApiHelper.handleAPIErrorIfNecessary(response);

            return response.data;
        } catch (e) {
            ApiHelper.handleAPIErrorIfNecessary(e?.response || "response not defined");
        }
    }

    /**
     * Internal route performing HTTP simple post
     * @param endpoint route endpoint (e.g. '/models')
     * @param data The data to send as body
     * @return {Promise<APIdata>}
     * @param progressWatcher
     */
    public async post(endpoint: string, data: any | FormData, progressWatcher?: ProgressWatcher): Promise<APIdata> {
        let url = this.generateURL(endpoint);
        let config: Axios.AxiosRequestConfig = {};

        if (data instanceof FormData) {
            console.log("data instanceof formdata :)")
            config.headers = {
                'content-type': 'multipart/form-data'
            };

            config.timeout = 1000/*ms*/ * 60/*s*/ * 60/*mn*/ * 24/*hr*/;
            config.onUploadProgress = progressWatcher?.onProgress;
        } else {
            console.log("data pas instance of formdata :(")
        }

        try {
            let response = await this.default.post(url, data, config);
            let apiData = response.data as APIdata;
            if (!apiData.content) {
                ApiHelper.handleAPIErrorIfNecessary(response);
            }

            return apiData;
        } catch (e) {
            ApiHelper.handleAPIErrorIfNecessary(e?.response || "response not defined");
            throw new APIError("Unknown error", 0, e.toString())
        }
    }

    /**
     * Internal route performing a HTTP put request
     * @param endpoint route endpoint (e.g. '/model/{modelId}')
     * @param data The data to send as body
     */
    public async put(endpoint: string, data: any | FormData): Promise<APIdata> {
        let url = this.generateURL(endpoint);
        let config: Axios.AxiosRequestConfig = {};

        if (data instanceof FormData) {
            config.headers = {
                'content-type': 'multipart/form-data'
            };
        }

        try {
            let response = await this.default.put(url, data, config);
            ApiHelper.handleAPIErrorIfNecessary(response);
            let apiData = response.data as APIdata;
            if (!apiData.content) {
                ApiHelper.handleAPIErrorIfNecessary(response);
            }

            return apiData;
        } catch (e) {
            ApiHelper.handleAPIErrorIfNecessary(e?.response || "response not defined");
            throw new APIError("Unknown error", 0, e.toString())
        }
    }

    /**
     * Internal route performing a HTTP delete request
     * @param endpoint route endpoint (e.g. '/model/{modelId}')
     */
    public async delete(endpoint: string): Promise<Boolean> {
        let url = this.generateURL(endpoint);

        try {
            const response = await this.default.delete(url);

            ApiHelper.handleAPIErrorIfNecessary(response);

            return true
        } catch (e) {
            ApiHelper.handleAPIErrorIfNecessary(e?.response || "response not defined");
            return false
        }
    }

    /**
     * Internal route performing a HTTP update request
     * @param endpoint route endpoint (e.g. '/model/{modelId}')
     * @param data The data to send as body
     */
    public async update(endpoint: string, data: any | FormData): Promise<APIdata> {
        return this.put(endpoint, data);
    }
}
